/**
 * Admin emails with unrestricted access to the admin dashboard.
 * Used by both frontend (immediate check) and backend (API middleware).
 */
export const ADMIN_EMAILS = ['prashant.jangid@heltar.com'] as const;

/**
 * Admin emails restricted to reading. These accounts may issue GET requests to
 * /v1/admin/* and nothing else. Route Mobile (parent company) operators live
 * here, alongside Heltar staff who need to see the dashboard but not act on it.
 */
export const READ_ONLY_ADMIN_EMAILS = [
  'aman.ray@routemobile.com',
  'amrita.jadhav@routemobile.com',
  'anand.chougala@routemobile.com',
  'aniket.tiwari@routemobile.com',
  'anjali.shaw@routemobile.com',
  'kiran.raykar@routemobile.com',
  'mohana.b@routemobile.com',
  'nithishkumar.shanmugaraj@routemobile.com',
  'punam.sandhya@routemobile.com',
  'sachin.ramapure@routemobile.com',
  'sahil.nilkanth@routemobile.com',
  'sri.sharvesh@routemobile.com',
  'srikanth.derangula@routemobile.com',
  'swarnava@heltar.com',
  'yaswanth.reddy@routemobile.com',
] as const;

export type AdminAccessLevel = 'full' | 'readonly' | 'none';

/**
 * Resolve what an email may do in the admin dashboard. `full` wins if an
 * address somehow appears on both lists.
 */
export const getAdminAccessLevel = (
  email: string | undefined | null,
): AdminAccessLevel => {
  if (!email) return 'none';
  const normalizedEmail = email.toLowerCase();

  if ((ADMIN_EMAILS as readonly string[]).includes(normalizedEmail)) {
    return 'full';
  }
  if ((READ_ONLY_ADMIN_EMAILS as readonly string[]).includes(normalizedEmail)) {
    return 'readonly';
  }
  return 'none';
};

/**
 * Check if an email may reach the admin dashboard at all, at any access level.
 */
export const isAdminEmail = (email: string | undefined | null): boolean => {
  return getAdminAccessLevel(email) !== 'none';
};

export const DOC_TYPE = {
  'text/plain': 'txt',
  'application/pdf': 'pdf',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/msword': 'doc',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'pptx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
};

export enum Category {
  MARKETING = 'MARKETING',
  UTILITY = 'UTILITY',
  AUTHENTICATION = 'AUTHENTICATION',
}

/** WhatsApp supported languages for templates */
export interface WhatsAppLanguage {
  label: string;
  value: string;
}

export const WHATSAPP_LANGUAGES: WhatsAppLanguage[] = [
  { label: 'Afrikaans', value: 'af' },
  { label: 'Albanian', value: 'sq' },
  { label: 'Arabic', value: 'ar' },
  { label: 'Azerbaijani', value: 'az' },
  { label: 'Bengali', value: 'bn' },
  { label: 'Bulgarian', value: 'bg' },
  { label: 'Catalan', value: 'ca' },
  { label: 'Chinese (CHN)', value: 'zh_CN' },
  { label: 'Chinese (HKG)', value: 'zh_HK' },
  { label: 'Chinese (TAI)', value: 'zh_TW' },
  { label: 'Croatian', value: 'hr' },
  { label: 'Czech', value: 'cs' },
  { label: 'Danish', value: 'da' },
  { label: 'Dutch', value: 'nl' },
  { label: 'English', value: 'en' },
  { label: 'English (UK)', value: 'en_GB' },
  { label: 'English (US)', value: 'en_US' },
  { label: 'Estonian', value: 'et' },
  { label: 'Filipino', value: 'fil' },
  { label: 'Finnish', value: 'fi' },
  { label: 'French', value: 'fr' },
  { label: 'Georgian', value: 'ka' },
  { label: 'German', value: 'de' },
  { label: 'Greek', value: 'el' },
  { label: 'Gujarati', value: 'gu' },
  { label: 'Hausa', value: 'ha' },
  { label: 'Hebrew', value: 'he' },
  { label: 'Hindi', value: 'hi' },
  { label: 'Hungarian', value: 'hu' },
  { label: 'Indonesian', value: 'id' },
  { label: 'Irish', value: 'ga' },
  { label: 'Italian', value: 'it' },
  { label: 'Japanese', value: 'ja' },
  { label: 'Kannada', value: 'kn' },
  { label: 'Kazakh', value: 'kk' },
  { label: 'Kinyarwanda', value: 'rw_RW' },
  { label: 'Korean', value: 'ko' },
  { label: 'Kyrgyz (Kyrgyzstan)', value: 'ky_KG' },
  { label: 'Lao', value: 'lo' },
  { label: 'Latvian', value: 'lv' },
  { label: 'Lithuanian', value: 'lt' },
  { label: 'Macedonian', value: 'mk' },
  { label: 'Malay', value: 'ms' },
  { label: 'Malayalam', value: 'ml' },
  { label: 'Marathi', value: 'mr' },
  { label: 'Norwegian', value: 'nb' },
  { label: 'Persian', value: 'fa' },
  { label: 'Polish', value: 'pl' },
  { label: 'Portuguese (BR)', value: 'pt_BR' },
  { label: 'Portuguese (POR)', value: 'pt_PT' },
  { label: 'Punjabi', value: 'pa' },
  { label: 'Romanian', value: 'ro' },
  { label: 'Russian', value: 'ru' },
  { label: 'Serbian', value: 'sr' },
  { label: 'Slovak', value: 'sk' },
  { label: 'Slovenian', value: 'sl' },
  { label: 'Spanish', value: 'es' },
  { label: 'Spanish (ARG)', value: 'es_AR' },
  { label: 'Spanish (SPA)', value: 'es_ES' },
  { label: 'Spanish (MEX)', value: 'es_MX' },
  { label: 'Swahili', value: 'sw' },
  { label: 'Swedish', value: 'sv' },
  { label: 'Tamil', value: 'ta' },
  { label: 'Telugu', value: 'te' },
  { label: 'Thai', value: 'th' },
  { label: 'Turkish', value: 'tr' },
  { label: 'Ukrainian', value: 'uk' },
  { label: 'Urdu', value: 'ur' },
  { label: 'Uzbek', value: 'uz' },
  { label: 'Vietnamese', value: 'vi' },
  { label: 'Zulu', value: 'zu' },
];

export {
  BillType,
  BILLABLE_STATUSES_STANDARD,
  BILLABLE_STATUSES_CONSERVATIVE,
  BILLABLE_STATUSES_RCS,
  getBillableStatuses,
} from './enums/billType';
export { ZohoCustomerType } from './enums/zohoCustomerType';
export { PaymentStatus } from './enums/paymentStatus';
export { PartnerType, PartnerStatus } from './enums/partnerType';
export { BooksIntegrationProvider } from './enums/booksIntegrationProvider';
export { TemplateMetaEventType } from './enums/templateMetaEvent';
export {
  DATA_LOCALIZATION_REGION_OPTIONS,
  DATA_LOCALIZATION_REGION_REGEX,
  isValidDataLocalizationRegion,
} from './enums/dataLocalizationRegion';

export * from './validation/auth';
export * from './validation/admin';
export * from './validation/analytics';
export * from './validation/apiKey';
export * from './validation/bizEmployee';
export * from './validation/campaign';
export * from './validation/partner';
export * from './validation/business';
export * from './validation/chatbot';
export * from './validation/clients';
export * from './validation/common';
export * from './validation/template';
export * from './validation/wallet';
export * from './pricing';
export * from './copilot';
export * from './editLock';
export * from './templateCopilot';
export * from './response';
export * from './livekit';
export * from './builtInTools';
export * from './messages';
export * from './groups';
export * from './gstin';
export * from './chatFlow';
export * from './clients';
export * from './metaAgent';

export const countryCodeToIso: Record<number, string> = {
  93: 'af', // Afghanistan
  355: 'al', // Albania
  213: 'dz', // Algeria
  376: 'ad', // Andorra
  244: 'ao', // Angola
  1268: 'ag', // Antigua and Barbuda
  54: 'ar', // Argentina
  374: 'am', // Armenia
  297: 'aw', // Aruba
  61: 'au', // Australia
  43: 'at', // Austria
  994: 'az', // Azerbaijan
  1242: 'bs', // Bahamas
  973: 'bh', // Bahrain
  880: 'bd', // Bangladesh
  1246: 'bb', // Barbados
  375: 'by', // Belarus
  32: 'be', // Belgium
  501: 'bz', // Belize
  229: 'bj', // Benin
  975: 'bt', // Bhutan
  591: 'bo', // Bolivia
  387: 'ba', // Bosnia and Herzegovina
  267: 'bw', // Botswana
  55: 'br', // Brazil
  246: 'io', // British Indian Ocean Territory
  673: 'bn', // Brunei
  359: 'bg', // Bulgaria
  226: 'bf', // Burkina Faso
  257: 'bi', // Burundi
  855: 'kh', // Cambodia
  237: 'cm', // Cameroon // 1: 'ca', // Canada
  238: 'cv', // Cape Verde
  599: 'bq', // Caribbean Netherlands
  236: 'cf', // Central African Republic
  235: 'td', // Chad
  56: 'cl', // Chile
  86: 'cn', // China
  57: 'co', // Colombia
  269: 'km', // Comoros
  243: 'cd', // Congo (DRC)
  242: 'cg', // Congo (Republic)
  506: 'cr', // Costa Rica
  225: 'ci', // Côte d’Ivoire
  385: 'hr', // Croatia
  53: 'cu', // Cuba // 599: 'cw', // Curaçao
  357: 'cy', // Cyprus
  420: 'cz', // Czech Republic
  45: 'dk', // Denmark
  253: 'dj', // Djibouti
  1767: 'dm', // Dominica // 1: 'do', // Dominican Republic
  593: 'ec', // Ecuador
  20: 'eg', // Egypt
  503: 'sv', // El Salvador
  240: 'gq', // Equatorial Guinea
  291: 'er', // Eritrea
  372: 'ee', // Estonia
  251: 'et', // Ethiopia
  679: 'fj', // Fiji
  358: 'fi', // Finland
  33: 'fr', // France
  594: 'gf', // French Guiana
  689: 'pf', // French Polynesia
  241: 'ga', // Gabon
  220: 'gm', // Gambia
  995: 'ge', // Georgia
  49: 'de', // Germany
  233: 'gh', // Ghana
  30: 'gr', // Greece
  1473: 'gd', // Grenada
  590: 'gp', // Guadeloupe
  1671: 'gu', // Guam
  502: 'gt', // Guatemala
  224: 'gn', // Guinea
  245: 'gw', // Guinea-Bissau
  592: 'gy', // Guyana
  509: 'ht', // Haiti
  504: 'hn', // Honduras
  852: 'hk', // Hong Kong
  36: 'hu', // Hungary
  354: 'is', // Iceland
  91: 'in', // India
  62: 'id', // Indonesia
  98: 'ir', // Iran
  964: 'iq', // Iraq
  353: 'ie', // Ireland
  972: 'il', // Israel
  39: 'it', // Italy
  1876: 'jm', // Jamaica
  81: 'jp', // Japan
  962: 'jo', // Jordan // 7: 'kz', // Kazakhstan
  254: 'ke', // Kenya
  686: 'ki', // Kiribati
  383: 'xk', // Kosovo
  965: 'kw', // Kuwait
  996: 'kg', // Kyrgyzstan
  856: 'la', // Laos
  371: 'lv', // Latvia
  961: 'lb', // Lebanon
  266: 'ls', // Lesotho
  231: 'lr', // Liberia
  218: 'ly', // Libya
  423: 'li', // Liechtenstein
  370: 'lt', // Lithuania
  352: 'lu', // Luxembourg
  853: 'mo', // Macau
  389: 'mk', // Macedonia
  261: 'mg', // Madagascar
  265: 'mw', // Malawi
  60: 'my', // Malaysia
  960: 'mv', // Maldives
  223: 'ml', // Mali
  356: 'mt', // Malta
  692: 'mh', // Marshall Islands
  596: 'mq', // Martinique
  222: 'mr', // Mauritania
  230: 'mu', // Mauritius
  52: 'mx', // Mexico
  691: 'fm', // Micronesia
  373: 'md', // Moldova
  377: 'mc', // Monaco
  976: 'mn', // Mongolia
  382: 'me', // Montenegro
  212: 'ma', // Morocco
  258: 'mz', // Mozambique
  95: 'mm', // Myanmar
  264: 'na', // Namibia
  674: 'nr', // Nauru
  977: 'np', // Nepal
  31: 'nl', // Netherlands
  687: 'nc', // New Caledonia
  64: 'nz', // New Zealand
  505: 'ni', // Nicaragua
  227: 'ne', // Niger
  234: 'ng', // Nigeria
  850: 'kp', // North Korea
  47: 'no', // Norway
  968: 'om', // Oman
  92: 'pk', // Pakistan
  680: 'pw', // Palau
  970: 'ps', // Palestine
  507: 'pa', // Panama
  675: 'pg', // Papua New Guinea
  595: 'py', // Paraguay
  51: 'pe', // Peru
  63: 'ph', // Philippines
  48: 'pl', // Poland
  351: 'pt', // Portugal // 1: 'pr', // Puerto Rico
  974: 'qa', // Qatar
  262: 're', // Réunion
  40: 'ro', // Romania
  7: 'ru', // Russia
  250: 'rw', // Rwanda
  1869: 'kn', // Saint Kitts and Nevis
  1758: 'lc', // Saint Lucia
  1784: 'vc', // Saint Vincent and the Grenadines
  685: 'ws', // Samoa
  378: 'sm', // San Marino
  239: 'st', // São Tomé and Príncipe
  966: 'sa', // Saudi Arabia
  221: 'sn', // Senegal
  381: 'rs', // Serbia
  248: 'sc', // Seychelles
  232: 'sl', // Sierra Leone
  65: 'sg', // Singapore
  421: 'sk', // Slovakia
  386: 'si', // Slovenia
  677: 'sb', // Solomon Islands
  252: 'so', // Somalia
  27: 'za', // South Africa
  82: 'kr', // South Korea
  211: 'ss', // South Sudan
  34: 'es', // Spain
  94: 'lk', // Sri Lanka
  249: 'sd', // Sudan
  597: 'sr', // Suriname
  268: 'sz', // Swaziland
  46: 'se', // Sweden
  41: 'ch', // Switzerland
  963: 'sy', // Syria
  886: 'tw', // Taiwan
  992: 'tj', // Tajikistan
  255: 'tz', // Tanzania
  66: 'th', // Thailand
  670: 'tl', // Timor-Leste
  228: 'tg', // Togo
  676: 'to', // Tonga
  1868: 'tt', // Trinidad and Tobago
  216: 'tn', // Tunisia
  90: 'tr', // Turkey
  993: 'tm', // Turkmenistan
  688: 'tv', // Tuvalu
  256: 'ug', // Uganda
  380: 'ua', // Ukraine
  971: 'ae', // United Arab Emirates
  44: 'gb', // United Kingdom
  1: 'us', // United States
  598: 'uy', // Uruguay
  998: 'uz', // Uzbekistan
  678: 'vu', // Vanuatu// 39: 'va', // Vatican City
  58: 've', // Venezuela
  84: 'vn', // Vietnam
  967: 'ye', // Yemen
  260: 'zm', // Zambia
  263: 'zw', // Zimbabwe
};

/**
 * Look up the uppercase ISO 2-letter code for a numeric country calling code.
 * Returns 'UNKNOWN' when the code is not in countryCodeToIso — downstream
 * pricing lookups fall back to DEFAULT_PRICING on 'UNKNOWN'.
 */
export const getIsoFromCountryCode = (
  countryCode: number | string | null | undefined,
): string => {
  if (countryCode === null || countryCode === undefined) return 'UNKNOWN';
  const numeric =
    typeof countryCode === 'number' ? countryCode : Number(countryCode);
  if (Number.isNaN(numeric)) return 'UNKNOWN';
  return countryCodeToIso[numeric]?.toUpperCase() ?? 'UNKNOWN';
};

/**
 * Reverse of countryCodeToIso: uppercase ISO 2-letter code -> numeric calling
 * code. Built once at load by inverting the map (first calling code wins, so
 * shared codes like +1 resolve to 'US'). NANP siblings without their own entry
 * (e.g. 'CA') are therefore absent -> undefined.
 */
const isoToCountryCodeMap: Record<string, number> = (() => {
  const map: Record<string, number> = {};
  for (const [code, iso] of Object.entries(countryCodeToIso)) {
    const upperIso = iso.toUpperCase();
    if (!(upperIso in map)) {
      map[upperIso] = Number(code);
    }
  }
  return map;
})();

/**
 * Reverse of {@link getIsoFromCountryCode}: numeric calling code for an
 * uppercase ISO 2-letter code (e.g. 'IN' -> 91, 'AE' -> 971), or undefined when
 * unknown. Callers default it (usually `?? 0`).
 */
export const getCountryCodeFromIso = (
  isoCode: string | null | undefined,
): number | undefined => {
  if (!isoCode) return undefined;
  return isoToCountryCodeMap[isoCode.toUpperCase()];
};

/**
 * Map country names (uppercase) to ISO 2-letter codes
 * Used for converting Karix CSV country names to ISO codes for pricing
 *
 * Note: Keys with spaces require quotes ('SOUTH KOREA'), while simple
 * identifiers without spaces can omit quotes (INDIA, POLAND).
 */
export const countryNameToIso: Record<string, string> = {
  INDIA: 'IN',
  'UNITED STATES': 'US',
  'UNITED KINGDOM': 'GB',
  'UNITED ARAB EMIRATES': 'AE',
  AUSTRALIA: 'AU',
  CANADA: 'CA',
  GERMANY: 'DE',
  FRANCE: 'FR',
  ITALY: 'IT',
  SPAIN: 'ES',
  NETHERLANDS: 'NL',
  BRAZIL: 'BR',
  MEXICO: 'MX',
  ARGENTINA: 'AR',
  COLOMBIA: 'CO',
  CHILE: 'CL',
  PERU: 'PE',
  INDONESIA: 'ID',
  MALAYSIA: 'MY',
  SINGAPORE: 'SG',
  THAILAND: 'TH',
  VIETNAM: 'VN',
  PHILIPPINES: 'PH',
  JAPAN: 'JP',
  'SOUTH KOREA': 'KR',
  'KOREA, REPUBLIC OF': 'KR',
  CHINA: 'CN',
  'HONG KONG': 'HK',
  TAIWAN: 'TW',
  RUSSIA: 'RU',
  TURKEY: 'TR',
  POLAND: 'PL',
  UKRAINE: 'UA',
  ROMANIA: 'RO',
  'CZECH REPUBLIC': 'CZ',
  HUNGARY: 'HU',
  EGYPT: 'EG',
  'SAUDI ARABIA': 'SA',
  'SOUTH AFRICA': 'ZA',
  NIGERIA: 'NG',
  KENYA: 'KE',
  PAKISTAN: 'PK',
  BANGLADESH: 'BD',
  'SRI LANKA': 'LK',
  NEPAL: 'NP',
  ISRAEL: 'IL',
  QATAR: 'QA',
  KUWAIT: 'KW',
  BAHRAIN: 'BH',
  OMAN: 'OM',
  JORDAN: 'JO',
  LEBANON: 'LB',
  IRAQ: 'IQ',
  IRAN: 'IR',
  IRELAND: 'IE',
  BELGIUM: 'BE',
  AUSTRIA: 'AT',
  SWITZERLAND: 'CH',
  SWEDEN: 'SE',
  NORWAY: 'NO',
  DENMARK: 'DK',
  FINLAND: 'FI',
  PORTUGAL: 'PT',
  GREECE: 'GR',
  'NEW ZEALAND': 'NZ',
  SENEGAL: 'SN',
  MOROCCO: 'MA',
  GHANA: 'GH',
  ETHIOPIA: 'ET',
  TANZANIA: 'TZ',
  UGANDA: 'UG',
  VENEZUELA: 'VE',
  ECUADOR: 'EC',
  BOLIVIA: 'BO',
  PARAGUAY: 'PY',
  URUGUAY: 'UY',
  'COSTA RICA': 'CR',
  PANAMA: 'PA',
  GUATEMALA: 'GT',
  HONDURAS: 'HN',
  'EL SALVADOR': 'SV',
  NICARAGUA: 'NI',
  JAMAICA: 'JM',
  LUXEMBOURG: 'LU',
  LATVIA: 'LV',
  // Additional countries from Karix CSV data
  ALBANIA: 'AL',
  BENIN: 'BJ',
  'BRUNEI DARUSSALAM': 'BN',
  BULGARIA: 'BG',
  'CONGO, THE DEMOCRATIC REPUBLIC OF THE': 'CD',
  CROATIA: 'HR',
  ESTONIA: 'EE',
  'ISLE OF MAN': 'IM',
  LITHUANIA: 'LT',
  'MACEDONIA, THE FORMER YUGOSLAV REPUBLIC OF': 'MK',
  MALDIVES: 'MV',
  MALTA: 'MT',
  MAURITIUS: 'MU',
  'MOLDOVA, REPUBLIC OF': 'MD',
  MYANMAR: 'MM',
  NAMIBIA: 'NA',
  'RUSSIAN FEDERATION': 'RU',
  SERBIA: 'RS',
  SLOVENIA: 'SI',
  'TANZANIA, UNITED REPUBLIC OF': 'TZ',
  UZBEKISTAN: 'UZ',
  ZAMBIA: 'ZM',
  ZIMBABWE: 'ZW',
};

export enum FallbackTriggerCondition {
  PAUSED = 'PAUSED',
  UTILITY_TO_MARKETING = 'UTILITY_TO_MARKETING',
}

export interface FallbackConfig {
  id: number;
  sourceTemplateName: string;
  sourceLanguageCode: string;
  triggerCondition: FallbackTriggerCondition;
  fallbackTemplateName: string;
  isEnabled: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export { SignUpType } from './enums/signUpType';

export type FieldName =
  | 'metaWebhooks'
  | 'cleverTapStatus'
  | 'cleverTapMessages'
  | 'sendEndpointFlowData'
  | 'webEngageStatus'
  | 'moEngage'
  | 'metaCustomFieldHook'
  | 'botWebhook'
  | 'frontendCampaignForward'
  | 'voiceInboundWebhook'
  | 'voiceLogsWebhook';

export enum Status {
  WAITING = 'waiting',
  FAILED = 'failed',
  EXPIRED = 'expired',

  SENT = 'sent',
  DELIVERED = 'delivered',
  READ = 'read',
  RESPONDED = 'responded',
  CLICKED = 'clicked',
  CLICKED_RESPONDED = 'clicked_responded',

  RECEIVED = 'received',
}

/**
 * How a campaign was created. `INTEGRATION` is the integrations API
 * "campaign forward" route (`/v1/integrations/campaign-forward`, e.g. the
 * PolicyBazaar/link flow) where button URLs are rewritten to tracked
 * pb-redirect links; `DIRECT` is a plain dashboard `/v1/campaigns/send`.
 * Persisted on `campaigns.source` and surfaced in the campaign list as an
 * Integration/Direct badge.
 */
export enum CampaignSource {
  DIRECT = 'direct',
  INTEGRATION = 'integration',
}

export const MIME_TYPES = {
  IMAGE: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  AUDIO: [
    'audio/aac',
    'audio/mp3',
    'audio/mpeg',
    'audio/amr',
    'audio/ogg',
    'audio/mp4',
    'audio/wav',
  ],
  VIDEO: [
    'video/mp4',
    'video/3gp',
    'video/3gpp',
    'video/mpeg',
    'video/quicktime',
  ],
  DOCUMENT: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'text/csv',
    'application/rtf',
    'application/vnd.oasis.opendocument.text-template',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.presentation',
  ],
  STICKER: ['image/webp'],
};

export const ALL_MIME_TYPES = [
  ...MIME_TYPES.IMAGE,
  ...MIME_TYPES.AUDIO,
  ...MIME_TYPES.VIDEO,
  ...MIME_TYPES.DOCUMENT,
];

export const getMediaType = (mimeType: string): string => {
  if (!mimeType) return 'document';
  if (mimeType.startsWith('image')) return 'image';
  if (mimeType.startsWith('audio')) return 'audio';
  if (mimeType.startsWith('video')) return 'video';
  return 'document';
};

export const trim = (inputString: string | undefined | null) => {
  if (!inputString) return inputString;
  return inputString
    .split(' ')
    .map(word => word.trim())
    .filter(word => word)
    .join(' ');
};

export const trimAll = (
  str: string | undefined | null,
): string | undefined | null => {
  if (!str) return str;
  return str.replace(/\s+/g, '');
};

export const trimEdges = (
  input: string | undefined | null,
): string | undefined | null => {
  return typeof input === 'string' ? input.trim() : input;
};

// Template Interactive Types (stored in message.interactive for template messages)
export type TemplateInteractiveButton = {
  type:
    | 'QUICK_REPLY'
    | 'URL'
    | 'PHONE_NUMBER'
    | 'CATALOG'
    | 'FLOW'
    | 'COPY_CODE';
  text: string;
  id?: string;
  url?: string;
  phone_number?: string;
  example?: string[];
  parameters?: Array<{
    type: 'text';
    text: string;
  }>;
};

/**
 * Meta Template Library - Button type
 */
export type LibraryTemplateButton = {
  type:
    | 'URL'
    | 'QUICK_REPLY'
    | 'PHONE_NUMBER'
    | 'FLOW'
    | 'CATALOG'
    | 'OTP'
    | 'COPY_CODE'
    | 'ORDER_DETAILS';
  text: string;
  url?: string;
  phone_number?: string;
};

/**
 * Meta Template Library - Template format from Meta's library API
 * GET /message_template_library?language={lang}&category={cat}
 */
export interface LibraryTemplate {
  id: string;
  name: string;
  language: string;
  category: 'UTILITY' | 'AUTHENTICATION';
  topic?: LibraryTemplateTopic | string;
  usecase?: LibraryTemplateUsecase | string;
  industry?: LibraryTemplateIndustry[];
  header?: string;
  header_params?: string[];
  header_param_types?: LibraryTemplateParamType[];
  body: string;
  body_params?: string[];
  footer?: string;
  body_param_types?: LibraryTemplateParamType[];
  buttons?: LibraryTemplateButton[];
  attributes?: string[];
}

/**
 * Library Template Filter - Industry
 */
export type LibraryTemplateIndustry =
  | 'E_COMMERCE'
  | 'FINANCIAL_SERVICES'
  | 'TELECOMMUNICATION';

/**
 * Library Template Filter - Topic
 */
export type LibraryTemplateTopic =
  | 'ACCOUNT_UPDATES'
  | 'CUSTOMER_FEEDBACK'
  | 'ORDER_MANAGEMENT'
  | 'PAYMENTS'
  | 'EVENT_REMINDER'
  | 'IDENTITY_VERIFICATION'
  | 'CALL_PERMISSIONS';

/**
 * Library Template Filter - Use case
 */
export type LibraryTemplateUsecase =
  | 'ACCOUNT_CREATION_CONFIRMATION'
  | 'AUTO_PAY_REMINDER'
  | 'DELIVERY_CONFIRMATION'
  | 'DELIVERY_FAILED'
  | 'DELIVERY_UPDATE'
  | 'FEEDBACK_SURVEY'
  | 'FRAUD_ALERT'
  | 'LOW_BALANCE_WARNING'
  | 'ORDER_ACTION_NEEDED'
  | 'ORDER_CONFIRMATION'
  | 'ORDER_DELAY'
  | 'ORDER_OR_TRANSACTION_CANCEL'
  | 'ORDER_PICK_UP'
  | 'PAYMENT_ACTION_REQUIRED'
  | 'PAYMENT_CONFIRMATION'
  | 'PAYMENT_DUE_REMINDER'
  | 'PAYMENT_OVERDUE'
  | 'PAYMENT_REJECT_FAIL'
  | 'PAYMENT_SCHEDULED'
  | 'RECEIPT_ATTACHMENT'
  | 'RETURN_CONFIRMATION'
  | 'SHIPMENT_CONFIRMATION'
  | 'STATEMENT_ATTACHMENT'
  | 'STATEMENT_AVAILABLE'
  | 'TRANSACTION_ALERT'
  | 'APPOINTMENT_CANCELLATION'
  | 'APPOINTMENT_CONFIRMATION'
  | 'APPOINTMENT_REMINDER'
  | 'APPOINTMENT_SCHEDULEING'
  | 'IN_PERSON_VERIFICATION'
  | 'CALL_PERMISSION_REQUEST';

/**
 * Library Template - Parameter types for body/header variables
 */
export type LibraryTemplateParamType =
  | 'TEXT'
  | 'ADDRESS'
  | 'AMOUNT'
  | 'DATE'
  | 'PHONE_NUMBER'
  | 'EMAIL'
  | 'NUMBER'
  | 'BUSINESS_NAME'
  | 'CODE'
  | 'GROUP_ID'
  | 'CARD_NUMBER';

/**
 * Library Template - Query parameters for filtering
 */
export interface LibraryTemplateFilters {
  language?: string;
  category?: 'UTILITY' | 'AUTHENTICATION';
  topic?: LibraryTemplateTopic;
  usecase?: LibraryTemplateUsecase;
  industry?: LibraryTemplateIndustry;
  search?: string;
}

export type TemplateInteractiveType = {
  type?: 'carousel';
  header?: {
    type: 'text' | 'image';
    text?: string;
    image?: {
      link: string;
    };
  };
  footer?: {
    type: 'text';
    text: string;
  };
  body?: {
    type: 'text';
    text: string;
  };
  cards?: Array<{
    header: {
      type: 'image' | 'video';
      image?: {
        link: string;
      };
      video?: {
        link: string;
      };
    };
    body: {
      type: 'text';
      text: string;
    };
    buttons: Array<TemplateInteractiveButton>;
  }>;
  buttons?: Array<TemplateInteractiveButton>;
  call_permission_request?: {
    type: 'call_permission_request';
  };
};
export * from './i18n/backendMessagesAr';
export * from './i18n/backendMessagesEs';
export * from './i18n/backendMessagesMs';
export * from './i18n/backendMessagesPt';
export * from './i18n/backendMessages';
export * from './creditLine';
export * from './externalId';
